import { useState } from 'react';
import { SERVER_URL } from '../lib/apiClient';
import { track } from '../lib/analytics';
import { useMaybeAuth } from '../hooks/useAuth.tsx';

const VT = "'VT323', monospace";
const ACCENT = '#86d46f';
const INK = '#c4d2c8';
const PANEL = '#070b09';
const EDGE = '#2a3a2f';

/** Above the Phaser canvas, the CRT overlay and the XP toast (all 9999). */
const LAYER = 10_000;

type Phase = 'idle' | 'sending' | 'done' | 'error';

const field: React.CSSProperties = {
  background: PANEL,
  border: `1px solid ${EDGE}`,
  color: INK,
  fontFamily: VT,
  fontSize: 16,
  padding: '8px 10px',
  outline: 'none',
  width: '100%',
};

/**
 * Always-visible feedback affordance, mounted once for the whole app.
 *
 * Deliberately reachable while logged out: a visitor who bounces off the
 * landing page without ever creating an account is the one we most need
 * to hear from, and there is no other channel that reaches them.
 */
export default function FeedbackButton() {
  const auth = useMaybeAuth();
  const sessionEmail = auth?.user?.email ?? '';

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');

  function close() {
    setOpen(false);
    setPhase('idle');
    setMessage('');
  }

  async function send() {
    if (phase === 'sending') return;
    if (message.trim().length === 0) return;
    setPhase('sending');
    try {
      const res = await fetch(`${SERVER_URL}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          email: email || sessionEmail || undefined,
          userId: auth?.user?.id,
          path: window.location.pathname,
        }),
      });
      if (res.ok) {
        // Conversion only. Never pass the message or the address to analytics.
        track('feedback_submitted');
        setPhase('done');
      } else {
        setPhase('error');
      }
    } catch {
      setPhase('error');
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Send feedback"
        style={{
          position: 'fixed',
          right: 16,
          bottom: 16,
          zIndex: LAYER,
          background: PANEL,
          border: `1px solid ${ACCENT}`,
          color: ACCENT,
          fontFamily: VT,
          fontSize: 17,
          letterSpacing: '0.06em',
          padding: '8px 14px',
          cursor: 'pointer',
          boxShadow: '0 2px 12px rgba(0,0,0,0.45)',
        }}
      >
        ▸ FEEDBACK
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-label="Send feedback"
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: LAYER,
        width: 'min(340px, calc(100vw - 32px))',
        background: PANEL,
        border: `1px solid ${ACCENT}`,
        boxShadow: '0 4px 24px rgba(0,0,0,0.55)',
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontFamily: VT, fontSize: 17, color: ACCENT, letterSpacing: '0.06em' }}>
          ▸ TRANSMIT TO THE OVERSEER
        </span>
        <button
          type="button"
          onClick={close}
          aria-label="Close feedback"
          style={{ background: 'none', border: 'none', color: INK, fontFamily: VT, fontSize: 18, cursor: 'pointer', padding: 0, lineHeight: 1 }}
        >
          ✕
        </button>
      </div>

      {phase === 'done' ? (
        <p style={{ fontFamily: VT, fontSize: 17, color: ACCENT, margin: 0 }}>
          ▸ Signal received. Nicolas reads every one of these.
        </p>
      ) : (
        <>
          <label htmlFor="fb-msg" style={{ fontFamily: VT, fontSize: 15, color: INK, opacity: 0.75 }}>
            What broke, or what is missing?
          </label>
          <textarea
            id="fb-msg"
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={4000}
            placeholder="Be blunt. It is more useful than being kind."
            style={{ ...field, resize: 'vertical' }}
          />

          {!sessionEmail && (
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@domain.com (optional, so I can reply)"
              aria-label="Your email, optional"
              style={field}
            />
          )}

          {phase === 'error' && (
            <span style={{ fontFamily: VT, fontSize: 15, color: '#e05050' }}>
              Transmission failed — retry in a moment.
            </span>
          )}

          <button
            type="button"
            onClick={() => void send()}
            disabled={phase === 'sending'}
            aria-label="Send"
            style={{
              background: ACCENT,
              border: `1px solid ${ACCENT}`,
              color: '#06110a',
              fontFamily: VT,
              fontSize: 18,
              letterSpacing: '0.06em',
              padding: '6px 14px',
              cursor: 'pointer',
              opacity: phase === 'sending' ? 0.6 : 1,
            }}
          >
            {phase === 'sending' ? 'SENDING…' : 'SEND ▸'}
          </button>
        </>
      )}
    </div>
  );
}
